import React, {useState, useEffect} from 'react'
import {
    Box,
    Heading,
    Link,
    Image,
    Text,
    Wrap,
    WrapItem
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useCustomFetch from '../../../../hooks/useFetchContent'
import PropTypes from 'prop-types'
import {
    sectionContainerStyles,
    infoContainerstyles,
    linkTextStyles,
    contentContainerStyles
} from './index.style'
import LoadingSpinner from '../../../../components/loading-spinner'

const CategorySection = ({title = '', items}) => (
    <Box sx={sectionContainerStyles}>
        <Heading as="h2" size="lg" mb={4}>
            {title}
        </Heading>

        <Wrap spacing={{base: 2, md: 6}} mb={{base: 4, md: 6}} justify="center">
            {items.map((item, idx) => (
                <WrapItem key={idx} flexBasis={{base: '48%', md: '23%'}} justifyContent="center">
                    <Box textAlign="center" w="100%">
                        <Link sx={linkTextStyles} href={item.link}>
                            <Image
                                src={item.img}
                                alt={item.title}
                                objectFit="cover"
                                mx="auto"
                                maxW="100%"
                                title={item.title}
                            />
                            <Text>{item.title}</Text>
                        </Link>
                    </Box>
                </WrapItem>
            ))}
        </Wrap>
    </Box>
)

CategorySection.propTypes = {
    items: PropTypes.array,
    title: PropTypes.string
}

const CategoryLanding = ({category}) => {
    const {token} = useAccessToken()
    const {response, loading} = useCustomFetch(token, [
        `category-landing-contentMiddle:category:${category?.id}`,
        `category-landing-contentBottom:category:${category?.id}`,
        `category-landing-info:category:${category?.id}`
    ])

    const [categories, setCategories] = useState([])
    const [infoHtml, setInfoHtml] = useState('')
    const [contentHtml, setContentHtml] = useState('')

    useEffect(() => {
        if (!response) return

        const parser = new DOMParser()
        const doc = parser.parseFromString(response, 'text/html')

        const infoSection = doc.querySelector('#category-landing-info')
        const infoInnerHtml = infoSection?.innerHTML || ''
        setInfoHtml(infoInnerHtml)

        const contentSection = doc.querySelector('#category-landing-contentMiddle')
        if (contentSection) {
            const lazyImages = contentSection.querySelectorAll('img.lazyload')

            lazyImages.forEach((img) => {
                const dataSrc = img.getAttribute('data-src')
                if (dataSrc && dataSrc !== 'null') {
                    img.setAttribute('src', dataSrc)
                    img.removeAttribute('data-src')
                }
            })

            setContentHtml(contentSection.outerHTML)
        }

        const hasCategories = Array.isArray(category?.categories) && category.categories.length > 0

        if (!hasCategories) return

        let finalCategories = []

        if (hasCategories) {
            const categoriesData = category?.categories?.map((cat) => {
                const imgTag = cat?.c_headerMenuBanner || ''
                const srcMatch = imgTag.match(/src\s*=\s*["']?([^"'\s>]+)["']?/i)
                const imgUrl = srcMatch ? srcMatch[1] : ''

                return {
                    link: cat.c_categoryURL,
                    img: imgUrl,
                    title: cat.name
                }
            })

            finalCategories.push({
                title: category?.c_categoryLandingH1ForSEO || category?.name,
                items: categoriesData
            })
        }

        setCategories(finalCategories)
    }, [response, category])

    if (loading) {
        return <LoadingSpinner />
    }

    return (
        <>
            <Box className="categories">
                {categories?.map((category, index) => (
                    <CategorySection key={index} {...category} />
                ))}
            </Box>
            <Box sx={contentContainerStyles} dangerouslySetInnerHTML={{__html: contentHtml}} />
            {infoHtml.trim().length > 0 && (
                <Box sx={infoContainerstyles} dangerouslySetInnerHTML={{__html: infoHtml}} />
            )}
        </>
    )
}

CategoryLanding.propTypes = {
    category: PropTypes.object
}

export default CategoryLanding
